import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { AllCommunityModule, ColDef, ModuleRegistry } from "ag-grid-community";
import clsx from "clsx";
import { investmentMemoGridTheme } from "./agGridTheme";
import { formatDate } from "@/utils/misc/format";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getInvestmentCriteria } from "@/entities/CreditBox/CreditBox.queries";
import {
    CreditBoxResultDetail,
    CreditBoxResult,
} from "@/entities/CreditBox/CreditBox.types";
import { getSFDCData } from "@/entities/Deal/Deal.queries";

ModuleRegistry.registerModules([AllCommunityModule]);

type InvestmentCriteriaRow = {
    creditBoxId: string;
    creditBoxName: string;
    capitalProductType: string | null;
    score: number;
    value: CreditBoxResult;
    lastPull: string;
    details: CreditBoxResultDetail["details"];
};

interface InvestmentCriteriaTableProps {
    opportunityId?: string;
}

export function InvestmentCriteriaTable({
    opportunityId,
}: InvestmentCriteriaTableProps) {
    const sfdcData = useApiQuery(getSFDCData);
    const sfdcOpportunityId = opportunityId
        ? sfdcData.data?.find((data) => data.id === opportunityId)?.id
        : sfdcData.data?.[0]?.id;

    const { data: criteriaData, isLoading } = useApiQuery(
        getInvestmentCriteria,
        {
            params: { sfdcOpportunityId: sfdcOpportunityId || "" },
            // enabled: !!sfdcOpportunityId,
        },
    );

    const data = useMemo<InvestmentCriteriaRow[]>(() => {
        if (!criteriaData?.creditBoxResultDetails) return [];

        return criteriaData.creditBoxResultDetails
            .map((result) => {
                const capitalProductCriteria = result.details[0]?.details?.find(
                    (d) => d.featureName === "Capital Product Type",
                );

                return {
                    creditBoxId: result.creditBoxId,
                    creditBoxName: result.creditBoxName,
                    capitalProductType:
                        (capitalProductCriteria?.creditBoxValue as string) ||
                        null,
                    score: result.score,
                    value:
                        result.score > 0
                            ? CreditBoxResult.PASS
                            : CreditBoxResult.FAIL,
                    lastPull: result.updatedAt,
                    details: result.details,
                };
            })
            .sort((a, b) => {
                if (
                    a.value === CreditBoxResult.PASS &&
                    b.value === CreditBoxResult.FAIL
                )
                    return -1;
                if (
                    a.value === CreditBoxResult.FAIL &&
                    b.value === CreditBoxResult.PASS
                )
                    return 1;
                return 0;
            });
    }, [criteriaData]);
    const tableHeight = useMemo(() => {
        const rowCount = data.length;
        const headerHeight = 42;
        const rowHeight = 52;
        const calculatedHeight = headerHeight + rowCount * rowHeight;
        const maxHeight = 400;
        return Math.min(calculatedHeight, maxHeight);
    }, [data.length]);

    const columnDefs = useMemo<ColDef<InvestmentCriteriaRow>[]>(
        () => [
            {
                field: "creditBoxName",
                headerName: "Strategy Name",
                headerClass: "heading-s",
                flex: 2,
                minWidth: 200,
                suppressHeaderMenuButton: true,
            },
            {
                field: "capitalProductType",
                headerName: "Capital Product Type",
                headerClass: "heading-s",
                flex: 1.5,
                minWidth: 180,
                suppressHeaderMenuButton: true,
            },
            {
                field: "score",
                headerName: "Score",
                headerClass: "heading-s",
                flex: 0.8,
                minWidth: 100,
                suppressHeaderMenuButton: true,
                valueFormatter: ({ value }) => {
                    return value?.toFixed(1) || "-";
                },
            },
            {
                field: "value",
                headerName: "Value",
                headerClass: "heading-s",
                flex: 1,
                minWidth: 120,
                suppressHeaderMenuButton: true,
                cellRenderer: ({ value }: { value: CreditBoxResult }) => {
                    const valueColors = {
                        [CreditBoxResult.PASS]: "bg-green-200",
                        [CreditBoxResult.FAIL]: "bg-red-200",
                    };
                    const colorClass = valueColors[value] || "bg-gray-200";
                    return (
                        <div
                            className={clsx(
                                "lozenge w-max rounded-xl px-2 py-1 text-brand-900",
                                colorClass,
                            )}
                        >
                            {value}
                        </div>
                    );
                },
            },
            {
                field: "lastPull",
                headerName: "Last Pull",
                headerClass: "heading-s",
                flex: 1,
                minWidth: 140,
                suppressHeaderMenuButton: true,
                valueFormatter: ({ value }) => {
                    if (!value) return "-";
                    return formatDate(value, { hideTime: true });
                },
            },
        ],
        [],
    );

    const defaultColDef = useMemo<ColDef>(
        () => ({
            resizable: true,
            sortable: false,
            filter: false,
            suppressHeaderMenuButton: true,
            suppressCellFlash: true,
            animateRows: false,
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        }),
        [],
    );

    if (isLoading) {
        return <div className="h-64 animate-pulse rounded-xl bg-slate-800" />;
    }

    if (!data || data.length === 0) {
        return (
            <div
                className={clsx(
                    "h-32 bg-slate-800/50",
                    "border border-slate-700",
                    "flex items-center justify-center rounded-xl",
                )}
            >
                <p className="text-slate-400">
                    No investment criteria available
                </p>
            </div>
        );
    }

    return (
        <div
            className={clsx("ag-theme-quartz-dark rounded")}
            style={{ height: `${tableHeight}px` }}
        >
            <AgGridReact<InvestmentCriteriaRow>
                theme={investmentMemoGridTheme}
                rowData={data}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                suppressAnimationFrame={true}
                suppressRowTransform={true}
                animateRows={false}
            />
        </div>
    );
}

export type { InvestmentCriteriaRow };
